import React from 'react';

import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';

import { QueryDescription } from './QueryDescription';
import { QueryLimit } from './QueryLimit';
import { QueryName } from './QueryName';
import { QueryOrder } from './QueryOrder';
import { QueryOrderBy } from './QueryOrderBy';
import { QueryShortest } from './QueryShortest';
import { QueryType } from './QueryType';

interface QueryBuilderHeaderProps {
  editable: boolean;
}

export function QueryBuilderHeader({ editable }: QueryBuilderHeaderProps) {
  const { queryState, dispatch } = useQueryBuilder();

  // Safety check (shouldn't happen in normal flow)
  if (!queryState) return null;

  const metadata = queryState.metadata;

  // Change handlers for editable inputs
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'UPDATE_METADATA',
      metadata: { name: e.target.value },
    });
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    dispatch({
      type: 'UPDATE_METADATA',
      metadata: { description: e.target.value },
    });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch({
      type: 'UPDATE_METADATA',
      metadata: { type: e.target.value as 'graph' | 'table' },
    });
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    if (value === undefined || (!isNaN(value) && value > 0)) {
      dispatch({
        type: 'UPDATE_METADATA',
        metadata: { limit: value },
      });
    }
  };

  const handleOrderByChange = (orderBy: string | undefined) => {
    dispatch({
      type: 'UPDATE_METADATA',
      metadata: { orderBy },
    });
  };

  const handleOrderChange = (descending: boolean) => {
    dispatch({
      type: 'UPDATE_METADATA',
      metadata: { descending },
    });
  };

  const handleShortestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      e.target.value === '' ? undefined : parseInt(e.target.value, 10);
    if (value === undefined || (!isNaN(value) && value > 0)) {
      dispatch({
        type: 'UPDATE_METADATA',
        metadata: { shortest: value },
      });
    }
  };

  return (
    <div className="min-w-0 flex-1 overflow-hidden">
      <QueryName
        value={metadata.name}
        editable={editable}
        description={metadata.description}
        onChange={handleNameChange}
      />

      <div className="mt-2 flex flex-wrap items-center gap-4">
        <QueryType
          value={metadata.type}
          editable={editable}
          onChange={handleTypeChange}
        />

        <QueryLimit
          value={metadata.limit}
          editable={editable}
          onChange={handleLimitChange}
        />

        {(queryState.metadata.type === 'table' ||
          queryState.metadata.type === 'table-tree') && (
          <>
            <QueryOrderBy
              value={metadata.orderBy}
              editable={editable}
              rootLabel={
                queryState.flatGraph.nodes.get(queryState.flatGraph.rootId)
                  ?.data.labels?.[0]
              }
              onChange={handleOrderByChange}
            />
            {metadata.orderBy && (
              <QueryOrder
                value={metadata.descending}
                editable={editable}
                onChange={handleOrderChange}
              />
            )}
          </>
        )}

        {queryState.metadata.type === 'graph' && (
          <QueryShortest
            value={metadata.shortest}
            editable={editable}
            onChange={handleShortestChange}
          />
        )}
      </div>

      <QueryDescription
        value={metadata.description}
        editable={editable}
        onChange={handleDescriptionChange}
      />
    </div>
  );
}
